import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { eventOsApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import { clearLastEventId, writeLastEventId, workspaceNav } from "../../routes/workspaceNavigation";
import { EventListItem } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { EventOSSidebar } from "./components/EventOSSidebar";
import { EventOSTabContent } from "./components/EventOSTabContent";
import { EventOSTopbar } from "./components/EventOSTopbar";
import { useEventOSData } from "./hooks/useEventOSData";
import { eventOsTabs, pnlTabs, TabId, WorkspaceArea } from "./types";
import "./styles/eventos-tokens-bridge.css";
import "./EventOSPage.css";

type Props = {
  area?: WorkspaceArea;
  workspace: WorkspaceId;
  embedMode?: boolean;
};

const EventOSPage: React.FC<Props> = ({ area = "event-os", workspace, embedMode = false }) => {
  const allowedTabs = useMemo<TabId[]>(
    () => (area === "event-os" ? [...eventOsTabs] : [...pnlTabs]),
    [area],
  );
  const navigate = useNavigate();
  const { eventId: routeEventId, tab: routeTab } = useParams<{ eventId?: string; tab?: string }>();
  const [clock, setClock] = useState(() => new Date().toLocaleTimeString());
  const [focusMode, setFocusMode] = useState(false);
  const { token } = useAuth();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(routeEventId ?? null);
  const tab = useMemo<TabId>(() => {
    if (embedMode) return "live";
    if (routeTab && allowedTabs.includes(routeTab as TabId)) {
      return routeTab as TabId;
    }
    return allowedTabs[0];
  }, [allowedTabs, embedMode, routeTab]);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventOsApi.listEvents(token!),
    enabled: Boolean(token),
  });

  const events = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);
  const loadingEvents = eventsQuery.isPending;
  const localError =
    eventsQuery.error instanceof Error ? eventsQuery.error.message : eventsQuery.error ? String(eventsQuery.error) : null;

  const { data, status, error, refetch: refetchWorkspace, isFetching: workspaceFetching } = useEventOSData(token, selectedEventId, area);

  useEffect(() => {
    if (!routeEventId) {
      // No event in URL -> drop any stale sticky id and bounce so the next
      // "Command Center" click re-picks via the entry redirect.
      clearLastEventId(workspace);
      navigate(workspaceNav.events(workspace), { replace: true });
      return;
    }

    // Persist eagerly so the sticky id survives even if the events query
    // hasn't resolved yet on this render.
    writeLastEventId(workspace, routeEventId);

    if (eventsQuery.isPending || eventsQuery.isFetching) return;
    if (events.length === 0) return;

    const eventExists = events.some((event) => event.id === routeEventId);
    if (!eventExists) {
      // The event we navigated to no longer exists for this user. Drop the
      // sticky id so the entry redirect picks a fresh default next time.
      clearLastEventId(workspace);
      navigate(workspaceNav.events(workspace), { replace: true });
      return;
    }

    setSelectedEventId(routeEventId);

    if (!embedMode && (!routeTab || !allowedTabs.includes(routeTab as TabId))) {
      navigate(workspaceNav.commandCenter(workspace, routeEventId, allowedTabs[0]), { replace: true });
    }
  }, [allowedTabs, embedMode, events, eventsQuery.isFetching, eventsQuery.isPending, navigate, routeEventId, routeTab, workspace]);

  React.useEffect(() => {
    if (embedMode) return;
    const timer = window.setInterval(() => {
      setClock(new Date().toLocaleTimeString());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [embedMode]);

  useEffect(() => {
    if (!embedMode) return;
    if (focusMode) {
      document.body.classList.add("eventos-focus-mode");
    } else {
      document.body.classList.remove("eventos-focus-mode");
    }
    return () => document.body.classList.remove("eventos-focus-mode");
  }, [embedMode, focusMode]);

  const handleSelectTab = (nextTab: TabId) => {
    if (!selectedEventId) return;
    navigate(workspaceNav.commandCenter(workspace, selectedEventId, nextTab));
  };

  if (embedMode) {
    return (
      <div className={`eventos-shell eventos-shell--in-app eventos-shell--embedded${focusMode ? " eventos-shell--focus" : ""}`}>
        <div className="eventos-embed-toolbar">
          <span className="eventos-embed-toolbar__label">Live Ops</span>
          <button
            type="button"
            className="eventos-nav-btn"
            onClick={() => setFocusMode((on) => !on)}
            aria-pressed={focusMode}
          >
            {focusMode ? "Exit focus" : "Focus mode"}
          </button>
        </div>
        {status === "loading" ? (
          <div className="eventos-skeleton-grid" aria-label="Loading live ops">
            <div className="eventos-skeleton-card">
              <div className="eventos-skeleton-line eventos-skeleton-line--title" />
              <div className="eventos-skeleton-line" />
            </div>
          </div>
        ) : null}
        {status === "error" ? (
          <div className="eventos-empty eventos-error-state">
            <span>{error || "Unable to load live ops."}</span>
            <button type="button" className="eventos-nav-btn" onClick={() => void refetchWorkspace()} disabled={workspaceFetching}>
              {workspaceFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}
        {selectedEventId && status === "ready" ? (
          <div className="eventos-embed-body">
            <EventOSTabContent tab={tab} data={data} area={area} eventId={selectedEventId} token={token} embedded />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`eventos-shell eventos-shell--in-app eventos-shell--${tab}`}>
      <EventOSSidebar area={area} tab={tab} onSelectTab={handleSelectTab} />

      <main className="eventos-main">
        <EventOSTopbar
          tab={tab}
          eventName={data.eventName}
          eventSubtitle={data.eventSubtitle}
          selectedEventId={selectedEventId}
          clock={clock}
          workspace={workspace}
        />
        <section className="eventos-content">
          {events.length === 0 && !loadingEvents ? (
            <div className="eventos-card">
              <div className="eventos-empty-events-illustration" aria-hidden="true">
                <span className="eventos-empty-events-icon">+</span>
              </div>
              <div className="eventos-label">No events yet</div>
              <p className="eventos-subtle eventos-empty-events-copy">
                Create an event first, then open the command center from the Events list.
              </p>
              <div className="eventos-empty-events-actions">
                <Link to={workspaceNav.newEvent(workspace)} className="eventos-primary-btn eventos-link-btn">
                  Add an event
                </Link>
                <Link to={workspaceNav.events(workspace)} className="eventos-nav-btn eventos-link-btn">
                  Browse events
                </Link>
              </div>
            </div>
          ) : null}
          {events.length > 0 ? (
            <div className="eventos-card">
              <div className="eventos-label">Active event</div>
              <MobileSelect
                label="Event"
                value={selectedEventId || ""}
                onChange={(nextEventId) => {
                  navigate(workspaceNav.commandCenter(workspace, nextEventId, tab));
                }}
                className="eventos-select"
                options={events.map((eventOption) => ({
                  value: eventOption.id,
                  label: `${eventOption.name} - ${eventOption.city}`,
                }))}
              />
            </div>
          ) : null}
          {loadingEvents ? (
            <div className="eventos-skeleton-card" aria-label="Loading events">
              <div className="eventos-skeleton-line eventos-skeleton-line--title" />
              <div className="eventos-skeleton-line" />
              <div className="eventos-skeleton-line eventos-skeleton-line--short" />
            </div>
          ) : null}
          {localError ? (
            <div className="eventos-empty eventos-error-state">
              <span>{localError}</span>
              <button type="button" className="eventos-nav-btn" onClick={() => void eventsQuery.refetch()} disabled={eventsQuery.isFetching}>
                {eventsQuery.isFetching ? "Retrying…" : "Retry events"}
              </button>
            </div>
          ) : null}
          {status === "loading" ? (
            <div className="eventos-skeleton-grid" aria-label="Loading workspace data">
              <div className="eventos-skeleton-card">
                <div className="eventos-skeleton-line eventos-skeleton-line--title" />
                <div className="eventos-skeleton-line" />
              </div>
              <div className="eventos-skeleton-card">
                <div className="eventos-skeleton-line eventos-skeleton-line--title" />
                <div className="eventos-skeleton-line" />
              </div>
            </div>
          ) : null}
          {status === "error" ? (
            <div className="eventos-empty eventos-error-state">
              <span>{error || "Unable to fetch command center data."}</span>
              <button type="button" className="eventos-nav-btn" onClick={() => void refetchWorkspace()} disabled={workspaceFetching}>
                {workspaceFetching ? "Retrying…" : "Retry"}
              </button>
            </div>
          ) : null}
          {selectedEventId && status === "ready" ? (
            <EventOSTabContent tab={tab} data={data} area={area} eventId={selectedEventId} token={token} />
          ) : null}
        </section>
      </main>
    </div>
  );
};

export default EventOSPage;
